import styled from 'styled-components';

import Link from '../UI/Link/Link';
import ButtonLink from '../UI/ButtonLink/ButtonLink';
import Image from '../UI/Image/Image';
import variables from '../../styles/variables';
import { KioskProps } from '../../types';
import PCOtag from '../../images/pts.svg';

interface ProductInfoProps extends KioskProps {
  isPhonesPage?: boolean;
}

export const ProductWrapper = styled.div`
  border-radius: 16px;
  min-height: 353px;
  width: 282px;
  background: ${variables.colorWhite};
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.10);
  transition: all 0.5s !important;
  position: relative;

  @media screen and (max-width: 1280px) {
    width: 100% !important;
  }

  @media screen and (max-width: 767px) {
    min-height: 353px;
  }
  
  &.phonePage {
    width: 100%;
  }

  &:hover,
  &:focus {
    z-index: 2;
    box-shadow: 0px 10px 30px 0px rgba(0, 0, 0, 0.15) !important;
  }
`;

export const Phone = styled.div<KioskProps>`
  display: flex;
  flex-direction: column;
  padding: 12px 0px;
  align-items: center;
  gap: 8px;
  @media screen and (max-width: 767px) {
    padding-bottom: 0px;
  }
`;

export const ProductLinkWrapper = styled.div<KioskProps>`
  width: 100%;
  max-width: ${({ isKiosk }) => (isKiosk ? '150px' : '50%')};

  @media screen and (max-width: 767px) {
    max-width: ${({ isKiosk }) => (isKiosk ? '100%' : '50%')};
  }
`;

interface ProductLinkProps extends KioskProps {
  loading?: boolean;
}

export const ProductLink = styled(Link)<ProductLinkProps>`
  width: 100%;
  height: 145px;
  position: relative;
  margin-bottom: 0;
  ${({ loading }) => loading && 'margin-bottom: 16px;'}

  display: block;
  /* TODO: Until CMS condition available, only present for iPhone 12 and 13 which has no solo back images */
  &.apple12Phone {
    height: auto;
    .gatsby-image-wrapper {
      width: 100%;
      height: auto;
    }
    @media screen and (max-width: 767px) {
      width: 100%;
      .gatsby-image-wrapper {
        width: auto;
        height: 100%;
      }
    }
  }
`;

export const BackImage = styled(Image)`
  width: 66%;
  position: absolute;
  left: 0;
  height: 100%;
  max-height: 200px;
  top: 0;
`;

export const FrontImage = styled(Image)`
  width: 66%;
  position: absolute;
  right: 0;
  height: 100%;
  max-height: 200px;
  top: 0;
`;

export const ProductInfo = styled.div<ProductInfoProps>`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0 12px 0 12px;
  @media screen and (max-width: 767px) {
    width: 100%;
    padding-top: 10px;
    ${({ isPhonesPage }) => isPhonesPage && 'align-items: center;'}
  }
`;

export const BrandText = styled.h4`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0px;
  line-height: 14px;
`;

export const ModelText = styled.p`
  font-size: 18px;
  font-weight: 600;
  line-height: 18px;
  margin-bottom: 12px;
`;

export const StyledLink = styled(Link)`
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 0;
  &:hover {
    text-decoration: none;
  }
  &.phone-title {
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    text-decoration: none;
    @media screen and (min-width: 768px) and (max-width: 1099px) {
      font-size: 17px;
    }
    @media screen and (min-width: 560px) and (max-width: 639px) {
      font-size: 16px;
    }
    @media screen and (max-width: 350px) {
      font-size: 17px;
    }
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const PriceContainer = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 24px;
  margin: 0.5rem 0 8px 0;
  @media screen and (max-width: 767px) {
    margin-top: 0px;
  }
`;

export const Price = styled.span`
  font-weight: 600;
  font-size: 24px;
  line-height: 24px;
  @media screen and (min-width: 560px) and (max-width: 640px) {
    font-size: 16px;
  }
  @media screen and (min-width: 768px) and (max-width: 1099px) {
    font-size: 18px;
  }
`;

export const Disclaimer = styled.span<KioskProps>`
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  margin-top: 5px;
  display: block;
  color: ${variables.colorBlack};
  @media screen and (max-width: 375px) {
    padding-right: 10px;
  }
`;

export const CTAWrapper = styled.div<{borderVisible: boolean; loading?: boolean;}>`
  border-top: ${({ borderVisible }) => (borderVisible ? `solid ${variables.colorGreyMedium} 1px` : 'initial')};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ loading }) => loading ? `center` : `space-between`};
  padding: 20px;
`;

export const StyledButton = styled(ButtonLink)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PromoWrapper = styled.div<{applyBackground?: boolean;}>`
  display: flex;
  padding: 7px 8px;
  align-items: center;
  gap: 8px;
  background: ${({ applyBackground }) => (applyBackground ? variables.colorPinkAlt : 'initial')};
  color: ${variables.colorRed3};
  font-size: 12px;
  font-weight: 400;
  line-height: 11px;
  justify-content: center;
  padding-bottom: 0px;
  min-height: 32px;
  @media screen and (max-width: 767px) {
    max-height: 32px;
  }
`;

export const PromoNumber = styled.span`
  font-size: 18px;
  color: ${variables.colorRed3};
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.075px;
  &:after {
    position: absolute;
    width: 1em;
    height: 1em;
    content: '';
    background-image: url(${PCOtag});
    background-size: 1em;
    background-repeat: no-repeat;
  }
`;

export const DisplayTag = styled.p`
  position: absolute;
  top: -0.8em;
  left: 1em;
  padding: 0.25em 0.5em;
  border-radius: 0.25em;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${variables.colorWhite};
  background-color: ${variables.colorBlack};
`;

export const CustomDisplayTag = styled.p`
  position: absolute;
  top: -0.8em;
  left: 1em;
  padding: 0.25em 0.5em;
  border-radius: 0.25em;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${variables.colorWhite};
  background-color: ${variables.colorRed};
`;

export const SkeletonTitle = styled.div`
  min-height: 30px;
  width: 150px;
  background-color: ${variables.colorGreyDisabled};
`;

export const SkeletonImage = styled.div`
  min-height: 150px;
  width: 100%;
  background-color: ${variables.colorGreyDisabled};
  border-radius: 10px;
  width: 90px;
  position: absolute;
  display: inline-block;
  vertical-align: top;
  margin-left: 22px;
  margin-right: 16px;
`;

export const SkeletonPrice = styled.div`
  min-height: 30px;
  width: 150px;
  background-color: ${variables.colorGreyDisabled};
  margin: auto;
`;

export const SkeletonPromo = styled.div`
  min-height: 20px;
  width: 190px;
  background-color: ${variables.colorGreyDisabled};
`;
